import { useState, useEffect, useMemo } from 'react'
import Select from "react-select";
import DatePicker from "react-datepicker";
import { useAddCampaignMutation, useGetReportingManagersQuery, useGetReportingUsersQuery, useLazyGetReportingSupervisorsQuery, useUpdateCampaignMutation } from "./Api";
import ToastrService from "../../services/toastService";
import Loader from "../loader/loader"

const CAMPAIGN_TYPES = [
    { label: 'Inbound', value: 'inbound' },
    { label: 'Outbound', value: 'outbound' },
    // { label: 'Both', value: 'both' },
];

const CAMPAIGN_STATUSES = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
];

const CHANNEL_OPTIONS = [
    { label: 'Voice', value: 'voice' },
    { label: 'Email', value: 'email' },
    { label: 'Chat', value: 'chat' },
];


const formatDateForApi = (date) => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const CampaignFormDrawer = ({ isOpen, mode, campaign, onSave, onClose }) => {

    const [addCampaign, { isLoading }] = useAddCampaignMutation();
    const [updateCampaign, { isLoading: updateIsLoading }] = useUpdateCampaignMutation();

    const { data: reportingUsersData } = useGetReportingUsersQuery(
        undefined,
        {
            skip: !isOpen,
        }
    );

    const { data: reportingManagersData } = useGetReportingManagersQuery(
        undefined,
        {
            skip: !isOpen,
        }
    );

    const [getReportingSupervisors] = useLazyGetReportingSupervisorsQuery();

    const queueOptions = useMemo(
        () =>
            (reportingUsersData?.data?.queues || []).map((queue) => ({
                id: queue.id,
                label: queue.name,
            })),
        [reportingUsersData]
    );

    const managerOptions = useMemo(
        () =>
            (reportingManagersData?.data?.managers || []).map((manager) => ({
                id: manager.id,
                label: manager.name,
            })),
        [reportingManagersData]
    );

    const loadSupervisors = async (managerId) => {
        try {
            const payLoad = {
                id: managerId,
            }
            const response = await getReportingSupervisors(payLoad).unwrap();

            const options = (response?.data?.supervisors || []).map(
                (supervisor) => ({
                    id: supervisor.id,
                    label: supervisor.name,
                })
            );

            setSupervisorOptions(options);
            return options;
        } catch (error) {
            console.error("Failed to load supervisors:", error);
            setSupervisorOptions([]);
            return [];
        }
    };

    const shiftTypeOptions = useMemo(
        () =>
            (reportingUsersData?.data?.shifts || []).map((shift) => ({
                id: shift.id,
                label: shift.name,
            })),
        [reportingUsersData]
    );

    const [campaignName, setCampaignName] = useState("");
    const [campaignType, setCampaignType] = useState(CAMPAIGN_TYPES[0].value);
    const [channel, setChannel] = useState(CHANNEL_OPTIONS[0].value);
    const [description, setDescription] = useState("");
    const [campaignManager, setCampaignManager] = useState(null);
    const [supervisors, setSupervisors] = useState([]);
    const [supervisorOptions, setSupervisorOptions] = useState([]);
    const [campaignStatus, setCampaignStatus] = useState(CAMPAIGN_STATUSES[0].value);

    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    const [shiftType, setShiftType] = useState(null);
    const [queueName, setQueueName] = useState([]);

    const [errors, setErrors] = useState({});


    const isFormValid = Boolean(
        campaignName.trim() &&
        campaignManager &&
        supervisors.length > 0 &&
        startDate &&
        endDate &&
        shiftType &&
        queueName.length > 0
    );

    const clearError = (field) => {
        setErrors((prev) => ({
            ...prev,
            [field]: ""
        }));
    };

    const setFieldError = (field, message) => {
        setErrors((prev) => ({
            ...prev,
            [field]: message
        }));
    };


    const validateForm = () => {
        const newErrors = {};

        if (!campaignName.trim()) {
            newErrors.campaignName = "Campaign Name is required";
        }

        if (!campaignManager) {
            newErrors.campaignManager = "Campaign Manager is required";
        }

        if (!supervisors.length) {
            newErrors.supervisors = "At least one Supervisor is required";
        }

        if (!startDate) {
            newErrors.campaignDate = "Campaign Date is required";
        }

        if (!shiftType) {
            newErrors.shiftType = "Shift Type is required";
        }

        if (!queueName.length) {
            newErrors.queueName = "At least one Queue is required";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };


    const handleCreateCampaign = async () => {
        try {
            if (!validateForm()) {
                return;
            }

            const payload = {
                campaignName: campaignName.trim(),
                campaignType,
                channel,
                description: description.trim(),
                campaignManager: campaignManager.id,
                campaignSupervisor: supervisors.map((supervisor) => supervisor.id),
                Status: campaignStatus,
                startDate: formatDateForApi(startDate),
                endDate: formatDateForApi(endDate),
                campaignShift: shiftType.id,
                queue: queueName.map((queue) => queue.id),
            };

            if (mode === "add") {
                const response = await addCampaign(payload);

                if (response?.data?.success) {
                    ToastrService.success(response?.data?.message);
                    resetForm();
                    onSave();
                } else {
                    ToastrService.error(response?.error?.data?.message || "Failed to create campaign");
                }
            } else {
                const updatePayload = {
                    id: campaign.id,
                    ...payload,
                };

                const response = await updateCampaign(updatePayload);

                if (response?.data?.success) {
                    ToastrService.success(response?.data?.message);
                    resetForm();
                    onSave();
                } else {
                    ToastrService.error(response?.error?.data?.message || "Failed to update campaign");
                }
            }

        } catch (err) {
            console.log(err, "err");
        }
    };

    const resetForm = () => {

        setCampaignName("");
        setCampaignType(CAMPAIGN_TYPES[0].value);
        setChannel(CHANNEL_OPTIONS[0].value);
        setDescription("");
        setCampaignManager(null);
        setSupervisors([]);
        setCampaignStatus(CAMPAIGN_STATUSES[0].value);
        setDateRange([null, null]);
        setShiftType(null);
        setQueueName([]);

        setErrors({});
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };


    useEffect(() => {
        if (!isOpen) return;

        // ADD MODE
        if (mode === "add") {
            resetForm();
            return;
        }

        // EDIT MODE
        if (mode === "edit" && campaign) {
            setCampaignName(campaign.campaign_name || "");

            setCampaignType(campaign.campaign_type || CAMPAIGN_TYPES[0].value);

            setChannel(campaign.channel || CHANNEL_OPTIONS[0].value);

            setDescription(campaign.description || "");

            setCampaignStatus(campaign.status || CAMPAIGN_STATUSES[0].value);

            // Manager
            const manager = managerOptions.find((item) => item.id === campaign.campaign_manager_id);

            if (manager) {
                setCampaignManager(manager);

                // Load supervisors for this manager
                loadSupervisors(manager.id).then((options) => {
                    const selectedSupervisors = options.filter(
                        (item) =>
                            campaign.campaign_supervisor_id?.includes(item.id)
                    );

                    setSupervisors(selectedSupervisors);
                });
            } else {
                setCampaignManager(null);
                setSupervisors([]);
                setSupervisorOptions([]);
            }


            // Shift
            const shift = shiftTypeOptions.find((item) => item.id === campaign.campaign_shift_id);

            setShiftType(shift || null);

            // Queue
            const selectedQueues = queueOptions.filter(
                (item) => campaign.queue?.includes(item.id)
            );
            setQueueName(selectedQueues);

            // Dates
            setDateRange([
                campaign.start_date
                    ? new Date(campaign.start_date)
                    : null,
                campaign.end_date
                    ? new Date(campaign.end_date)
                    : null
            ]);

            setErrors({});
        }
    }, [isOpen, mode, campaign, managerOptions, shiftTypeOptions, queueOptions]);


    return (
        <div>
            {isLoading || updateIsLoading ? <Loader /> :
                <>
                    {isOpen && (
                        <div
                            className="offcanvas-backdrop fade show"
                            onClick={handleClose}
                        ></div>
                    )}
                    <div
                        className={`offcanvas offcanvas-end ${isOpen ? "show" : ""}`}
                        tabIndex="-1"
                        style={{
                            visibility: isOpen ? "visible" : "hidden"
                        }}
                    >
                        <div className="offcanvas-header px-4 py-3">
                            <h3 className="mb-1 drawer-header">
                                {mode === "edit" ? "Edit Campaign" : "Create Campaign"}
                            </h3>
                            <button
                                type="button"
                                className="btn-close text-reset"
                                onClick={handleClose}
                            ></button>
                        </div>

                        <div className="offcanvas-body px-4 py-3 ">
                            <form autoComplete="off" noValidate>

                                <div className="mb-3">
                                    {/* Campaign Name */}
                                    <div className="col-md-12">
                                        <label className="form-label fw-semibold">
                                            Campaign Name <span className="text-danger">*</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. Inbound — Tier 1"
                                            value={campaignName}
                                            maxLength={50}
                                            onChange={(e) => {
                                                const value = e.target.value;

                                                setCampaignName(value);

                                                if (value.trim()) {
                                                    clearError("campaignName");
                                                } else {
                                                    setFieldError(
                                                        "campaignName",
                                                        "Campaign Name is required"
                                                    );
                                                }
                                            }}
                                        />
                                        {errors.campaignName && (
                                            <div className="text-danger small mt-1">
                                                {errors.campaignName}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Campaign Type + Channel */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">
                                            Campaign Type
                                        </label>

                                        <select className="form-select"
                                            value={campaignType}
                                            onChange={(e) => setCampaignType(e.target.value)}
                                        >
                                            {CAMPAIGN_TYPES.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">
                                            Channel
                                        </label>

                                        <select className="form-select"
                                            value={channel}
                                            onChange={(e) => setChannel(e.target.value)}
                                        >
                                            {CHANNEL_OPTIONS.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">
                                        Description
                                    </label>

                                    <textarea
                                        className="form-control"
                                        style={{ height: "120px", resize: "none" }}
                                        maxLength={200}
                                        placeholder="Brief description of this campaign..."
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    ></textarea>
                                    <div className="text-muted small text-end mt-1">
                                        {description.length}/200
                                    </div>
                                </div>

                                {/* Campaign Manager */}
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">
                                        Campaign Manager <span className="text-danger">*</span>
                                    </label>

                                    <Select
                                        options={managerOptions}
                                        placeholder="-- Select Manager --"
                                        isClearable
                                        classNamePrefix="react-select"
                                        getOptionValue={(option) => String(option.id)}
                                        getOptionLabel={(option) => option.label}
                                        menuPlacement="auto"
                                        value={campaignManager}
                                        onChange={(selected) => {
                                            setCampaignManager(selected);
                                            setSupervisors([]);
                                            setSupervisorOptions([]);

                                            if (!selected) {
                                                setFieldError(
                                                    "campaignManager",
                                                    "Campaign Manager is required"
                                                );
                                                clearError("supervisors");
                                                return;
                                            }

                                            clearError("campaignManager");
                                            clearError("supervisors");
                                            loadSupervisors(selected.id);
                                        }}
                                    />

                                    <div className="small text-muted mt-1">
                                        The manager will oversee this campaign and its reporting hierarchy.
                                    </div>

                                    {errors.campaignManager && (
                                        <div className="text-danger small mt-1">
                                            {errors.campaignManager}
                                        </div>
                                    )}
                                </div>


                                {/* Supervisors */}
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">
                                        Supervisors <span className="text-danger">*</span>
                                    </label>

                                    <Select
                                        options={supervisorOptions}
                                        placeholder="-- Select Supervisor --"
                                        isClearable
                                        isMulti
                                        isDisabled={!campaignManager}
                                        closeMenuOnSelect={false}
                                        classNamePrefix="react-select"
                                        menuPlacement="auto"
                                        getOptionValue={(option) => String(option.id)}
                                        getOptionLabel={(option) => option.label}
                                        value={supervisors}
                                        onChange={(selected) => {
                                            const value = selected || [];

                                            setSupervisors(value);

                                            if (value.length > 0) {
                                                clearError("supervisors");
                                            } else {
                                                setFieldError(
                                                    "supervisors",
                                                    "At least one Supervisor is required"
                                                );
                                            }
                                        }}
                                    />
                                    <div className="small text-muted mt-1">
                                        You can add more supervisors after campaign creation.
                                    </div>
                                    {errors.supervisors && (
                                        <div className="text-danger small mt-1">
                                            {errors.supervisors}
                                        </div>
                                    )}
                                </div>

                                {/* Status + Start Date */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">
                                            Campaign Status
                                        </label>

                                        <select className="form-select"
                                            value={campaignStatus}
                                            onChange={(e) => setCampaignStatus(e.target.value)}
                                        >
                                            {CAMPAIGN_STATUSES.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">
                                            Campaign Date <span className="text-danger">*</span>
                                        </label>

                                        <DatePicker
                                            selectsRange
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={(update) => {
                                                const [start, end] = update || [null, null];

                                                setDateRange([start, end]);
                                                if (start) {
                                                    clearError("campaignDate");
                                                } else {
                                                    setFieldError(
                                                        "campaignDate",
                                                        "Campaign Date is required"
                                                    );
                                                }
                                            }}
                                            isClearable
                                            placeholderText="Select date range"
                                            dateFormat="dd/MM/yyyy"
                                            className='form-control'
                                        />
                                        {errors.campaignDate && (
                                            <div className="text-danger small mt-1">
                                                {errors.campaignDate}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Shift Type */}
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">
                                        Shift Type <span className="text-danger">*</span>
                                    </label>

                                    <Select
                                        options={shiftTypeOptions}
                                        placeholder="-- Select Shift --"
                                        isClearable
                                        classNamePrefix="react-select"
                                        menuPlacement="auto"
                                        getOptionValue={(option) => String(option.id)}
                                        getOptionLabel={(option) => option.label}
                                        value={shiftType}
                                        onChange={(selected) => {
                                            setShiftType(selected);

                                            if (selected) {
                                                clearError("shiftType");
                                            } else {
                                                setFieldError(
                                                    "shiftType",
                                                    "Shift Type is required"
                                                );
                                            }
                                        }}
                                    />
                                    {errors.shiftType && (
                                        <div className="text-danger small mt-1">
                                            {errors.shiftType}
                                        </div>
                                    )}
                                </div>

                                {/* Queue Name */}
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">
                                        Queue Name <span className="text-danger">*</span>
                                    </label>

                                    <Select
                                        options={queueOptions}
                                        placeholder="-- Select Queue --"
                                        isClearable
                                        isMulti
                                        closeMenuOnSelect={false}
                                        classNamePrefix="react-select"
                                        menuPlacement="top"
                                        getOptionValue={(option) => String(option.id)}
                                        getOptionLabel={(option) => option.label}
                                        value={queueName}
                                        onChange={(selected) => {
                                            const value = selected || [];

                                            setQueueName(value);

                                            if (value.length > 0) {
                                                clearError("queueName");
                                            } else {
                                                setFieldError(
                                                    "queueName",
                                                    "At least one Queue is required"
                                                );
                                            }
                                        }}
                                    />
                                    <div className="small text-muted mt-1">
                                        Queue identifier used by the dialer to route calls to this campaign.
                                    </div>
                                    {errors.queueName && (
                                        <div className="text-danger small mt-1">
                                            {errors.queueName}
                                        </div>
                                    )}
                                </div>



                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-3 py-2"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-primary px-3 py-2"
                                        disabled={!isFormValid}
                                        onClick={handleCreateCampaign}
                                    >
                                        {mode === "edit" ? "Update Campaign" : "Create Campaign"}
                                    </button>
                                </div>

                            </form>
                        </div>
                    </div>
                </>
            }
        </div>


    )
}

export default CampaignFormDrawer